/**
 * Entitlements, SKUs and metering.
 *
 * Strategy constraints encoded here rather than left to each surface
 * (docs/08-monetization.md):
 *
 *  - Exactly four SKUs per market, enforced by a fixed-length tuple type. Verbi has ten
 *    live IAPs spanning four different annual prices, visible on their own store page.
 *  - No weekly SKU. It exists in competitors purely as an anchor and is the proximate
 *    cause of refund rage.
 *  - Live voice minutes are the ONLY metered resource. Everything cheap is unlimited,
 *    which makes the free tier generous and the margin controllable at once.
 */

export type Tier = 'free' | 'plus' | 'pro';
export type Period = 'monthly' | 'annual';
export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'grace'
  | 'expired'
  | 'refunded'
  | 'paused';

export interface Sku {
  readonly id: string;
  readonly tier: Exclude<Tier, 'free'>;
  readonly period: Period;
  readonly priceUsd: number;
  readonly trialDays: number;
  /** Shown as the price anchor, e.g. "$6.67/mo". */
  readonly effectiveMonthlyUsd: number;
}

/**
 * The complete SKU set. The tuple type pins the length at 3 paid SKUs (+ free), so
 * adding a fifth is a type error, not a product decision made in a hurry.
 */
export const SKUS = [
  {
    id: 'plus_monthly',
    tier: 'plus',
    period: 'monthly',
    priceUsd: 14.99,
    trialDays: 0,
    effectiveMonthlyUsd: 14.99,
  },
  {
    id: 'plus_annual',
    tier: 'plus',
    period: 'annual',
    priceUsd: 79.99,
    trialDays: 7,
    effectiveMonthlyUsd: 6.67,
  },
  {
    id: 'pro_annual',
    tier: 'pro',
    period: 'annual',
    priceUsd: 149.99,
    trialDays: 7,
    effectiveMonthlyUsd: 12.5,
  },
] as const satisfies readonly [Sku, Sku, Sku];

export const HERO_SKU_ID = 'plus_annual';

export function skuById(id: string): Sku | undefined {
  return SKUS.find((s) => s.id === id);
}

// ── Limits ────────────────────────────────────────────────────────────────────

export interface TierLimits {
  /** Daily live-voice allowance in seconds. `Infinity` means unmetered. */
  readonly voiceSecondsPerDay: number;
  /** Custom role-play generations per day. */
  readonly customScenariosPerDay: number;
  /** Days of report-card history visible. `Infinity` means all. */
  readonly historyDays: number;
  /** Scenarios accessible. `Infinity` means the full library. */
  readonly scenarioCount: number;
  readonly deepAnalysis: boolean;
  /**
   * Debate and Interview — the two conversation modes the tier table gives Plus and
   * Pro and not free.
   *
   * This field did not exist, so the boundary was published and unenforced: the tier
   * table has "Debate & Interview modes | — | ✓ | ✓", `04-screen-specs` D8 says the
   * Plus modes are "shown locked rather than hidden", `services/conversation.ts` calls
   * them "the reason someone pays for the tier" — and a free learner could open and
   * finish both on every client. The entry points were added when the screens turned
   * out to be unreachable, and the lock was not added with them.
   */
  readonly advancedModes: boolean;
  readonly offlineDownloads: boolean;
  readonly simultaneousCourses: number;
  readonly examTracks: boolean;
  readonly monthlyAssessment: boolean;
}

/** 10 minutes: enough for a full lesson plus a short scenario — a satisfying daily session. */
export const FREE_VOICE_SECONDS_PER_DAY = 600;

export const LIMITS: Readonly<Record<Tier, TierLimits>> = {
  free: {
    voiceSecondsPerDay: FREE_VOICE_SECONDS_PER_DAY,
    customScenariosPerDay: 1,
    historyDays: 7,
    scenarioCount: 40,
    deepAnalysis: false,
    advancedModes: false,
    offlineDownloads: false,
    simultaneousCourses: 1,
    examTracks: false,
    monthlyAssessment: false,
  },
  plus: {
    voiceSecondsPerDay: Number.POSITIVE_INFINITY,
    customScenariosPerDay: Number.POSITIVE_INFINITY,
    historyDays: Number.POSITIVE_INFINITY,
    scenarioCount: Number.POSITIVE_INFINITY,
    deepAnalysis: true,
    advancedModes: true,
    offlineDownloads: true,
    simultaneousCourses: 1,
    examTracks: false,
    monthlyAssessment: false,
  },
  pro: {
    voiceSecondsPerDay: Number.POSITIVE_INFINITY,
    customScenariosPerDay: Number.POSITIVE_INFINITY,
    historyDays: Number.POSITIVE_INFINITY,
    scenarioCount: Number.POSITIVE_INFINITY,
    deepAnalysis: true,
    advancedModes: true,
    offlineDownloads: true,
    simultaneousCourses: 2,
    examTracks: true,
    monthlyAssessment: true,
  },
};

/**
 * Capabilities that are free on every tier, forever. Listed explicitly so that a future
 * "let's gate this" conversation has to delete a line from this array and face it.
 */
export const ALWAYS_FREE = [
  'srs_review',
  'swipe_deck',
  'word_games',
  'grammar_practice',
  'conjugation_trainer',
  'texting_practice',
  'listening_library',
  'dictation',
  'text_conversation',
  'basic_report_card',
  'captions',
  'tap_to_toggle_mic',
  'text_scaling',
  'colorblind_scores',
  'data_export',
] as const;

export type AlwaysFreeCapability = (typeof ALWAYS_FREE)[number];

// ── Entitlement resolution ────────────────────────────────────────────────────

/**
 * Where a subscription is held, and therefore where it can be cancelled.
 *
 * Only the holder can cancel: an Apple subscription cannot be ended from the web, and a
 * Play subscription cannot be ended from an iPhone. Ours used to guess — the web client
 * sent everyone to Apple — and the learner who subscribed on Android landed on a page
 * where their subscription does not appear.
 *
 * That is the single commonest complaint class in the recorded category: Loora, at $2M
 * a month, has one-star reviews reading "for cancel subscription you need going through
 * their app, you didn't see you subscribe in your phone" and
 * "I cancelled my membership 2 years ago, but they kept charging me"
 * (`research/12` fourth pass). Most of that is not malice, it is this bug.
 */
export type SubscriptionStore = 'apple' | 'google' | 'stripe' | 'promo';

export interface SubscriptionRecord {
  readonly tier: Exclude<Tier, 'free'>;
  readonly status: SubscriptionStatus;
  readonly currentPeriodEnd: number;
  readonly trialEndsAt: number | null;
  readonly store: SubscriptionStore;
}

export interface Entitlement {
  readonly tier: Tier;
  readonly limits: TierLimits;
  readonly status: SubscriptionStatus | null;
  readonly periodEnd: number | null;
  readonly inTrial: boolean;
  readonly inGrace: boolean;
  /**
   * Which store holds it, so a client can send the learner to the one place it can
   * actually be cancelled. Null when there is nothing to cancel.
   */
  readonly store: SubscriptionStore | null;
}

/** Days of full access granted after a failed renewal before dropping to free. */
export const GRACE_DAYS = 3;

/**
 * Resolves the effective entitlement. Note that `expired` and `refunded` fall back to
 * `free` — never to a locked state. A lapsed subscriber keeps everything they learned
 * and simply returns to free limits.
 */
export function resolveEntitlement(
  subscription: SubscriptionRecord | null,
  now: number,
): Entitlement {
  if (subscription === null) {
    return {
      tier: 'free',
      limits: LIMITS.free,
      status: null,
      periodEnd: null,
      inTrial: false,
      inGrace: false,
      store: null,
    };
  }

  const { tier, status, currentPeriodEnd, trialEndsAt, store } = subscription;
  const inTrial = status === 'trialing' && trialEndsAt !== null && now < trialEndsAt;
  const active = status === 'active' || inTrial;
  const inGrace = status === 'grace' && now < currentPeriodEnd + GRACE_DAYS * 86_400_000;

  if (active || inGrace) {
    return {
      tier,
      limits: LIMITS[tier],
      status,
      periodEnd: currentPeriodEnd,
      inTrial,
      inGrace,
      store,
    };
  }

  return {
    tier: 'free',
    limits: LIMITS.free,
    status,
    periodEnd: currentPeriodEnd,
    inTrial: false,
    inGrace: false,
    // Kept past expiry on purpose. A lapsed subscriber still has a row in a store and
    // may still be being charged by it, and "you are on the free tier" is not an answer
    // to "why did my card get billed again".
    store,
  };
}

/**
 * Where the learner must go to cancel, and what to call it.
 *
 * `url` is null for a store with no self-serve page: a promotional month was never
 * bought, so there is nothing to cancel and saying so is the honest answer.
 */
export interface CancellationRoute {
  readonly store: SubscriptionStore;
  readonly label: string;
  readonly url: string | null;
  /** What the learner reads. Names the store, because not knowing is the whole problem. */
  readonly explanation: string;
}

const CANCELLATION: Readonly<Record<SubscriptionStore, CancellationRoute>> = {
  apple: {
    store: 'apple',
    label: 'Manage in the App Store',
    url: 'https://apps.apple.com/account/subscriptions',
    explanation:
      'You subscribed through Apple, so the subscription lives in your Apple account — ' +
      'we cannot cancel it for you, and nobody else can either.',
  },
  google: {
    store: 'google',
    label: 'Manage in Google Play',
    url: 'https://play.google.com/store/account/subscriptions',
    explanation:
      'You subscribed through Google Play, so the subscription lives in your Google ' +
      'account — we cannot cancel it for you, and nobody else can either.',
  },
  stripe: {
    store: 'stripe',
    label: 'Ask us to cancel it',
    // No URL on purpose. There is no Stripe customer portal in this build, and
    // inventing `/me/billing` — which is what the first draft of this did — put a dead
    // link in the one flow whose whole point is that it is not a dead end. The gate
    // `pnpm dead-links` caught it, which is why that gate exists.
    url: null,
    explanation:
      'You subscribed on the web, so we hold this one — and there is no self-serve page for it yet. Ask below and a person cancels it; that is a real route, not a queue.',
  },
  promo: {
    store: 'promo',
    label: 'Nothing to cancel',
    url: null,
    explanation:
      'This month was given, not bought. There is no payment to stop and nothing will ' +
      'be charged when it ends.',
  },
};

/**
 * The one place this subscription can be cancelled, or null when there is none.
 *
 * Takes the store rather than the platform the learner happens to be holding, which is
 * the bug this replaces: the right answer for someone who subscribed on Android and
 * opened the web app is Google Play, not "the store you are standing in".
 */
export function cancellationRoute(store: SubscriptionStore | null): CancellationRoute | null {
  return store === null ? null : CANCELLATION[store];
}

// ── Metering ──────────────────────────────────────────────────────────────────

export interface UsageSnapshot {
  readonly voiceSecondsUsed: number;
  readonly customScenariosUsed: number;
  /**
   * Seconds granted on top of today's allowance — currently only by the dismissal gift
   * (`giftOnDismiss`). Optional because every existing caller predates it and a missing
   * bonus must mean zero, not `NaN`: this value is subtracted from in the quota maths,
   * and an undefined leaking in would make the meter read as unmetered.
   */
  readonly bonusVoiceSeconds?: number;
}

export type MeteredLimit = 'voice_minutes' | 'custom_scenarios';

export interface QuotaCheck {
  readonly allowed: boolean;
  readonly limit: MeteredLimit | null;
  readonly remaining: number;
  /** `Infinity` for unmetered tiers; drives the "Unlimited" meter state. */
  readonly total: number;
}

export function checkVoiceQuota(entitlement: Entitlement, usage: UsageSnapshot): QuotaCheck {
  // The gift is real minutes on the metered axis, so it has to widen the budget here
  // rather than anywhere cosmetic. `Infinity + bonus` is still `Infinity`, so an
  // unmetered tier is unaffected.
  const total = entitlement.limits.voiceSecondsPerDay + (usage.bonusVoiceSeconds ?? 0);
  const remaining = total - usage.voiceSecondsUsed;
  return {
    allowed: remaining > 0,
    limit: remaining > 0 ? null : 'voice_minutes',
    remaining: Math.max(0, remaining),
    total,
  };
}

export function checkCustomScenarioQuota(
  entitlement: Entitlement,
  usage: UsageSnapshot,
): QuotaCheck {
  const total = entitlement.limits.customScenariosPerDay;
  const remaining = total - usage.customScenariosUsed;
  return {
    allowed: remaining > 0,
    limit: remaining > 0 ? null : 'custom_scenarios',
    remaining: Math.max(0, remaining),
    total,
  };
}

/**
 * A turn that has already started always completes, even if it crosses the quota
 * boundary. Cutting a learner off mid-sentence to show a paywall is the single most
 * hostile thing a metered product can do.
 */
export function mayStartTurn(check: QuotaCheck): boolean {
  return check.allowed;
}

/**
 * The oldest timestamp a tier may see history from, as epoch ms.
 *
 * `LIMITS[tier].historyDays` has always been the definition, and the Worker's
 * `listReports` carried its own `Date.now() - 7 * 86_400_000` beside it — so the
 * number was declared in the domain and enforced from a copy, and changing the
 * allowance would have moved the paywall prompt without moving the data behind it.
 *
 * The cut is on *age*, not on count: losing yesterday's report because you had a busy
 * week would be indefensible. An unlimited tier returns 0, which is every row.
 */
export function historyHorizon(tier: Tier, now: number): number {
  const days = LIMITS[tier].historyDays;
  return Number.isFinite(days) ? now - days * 86_400_000 : 0;
}

export function isUnlimited(total: number): boolean {
  return !Number.isFinite(total);
}

/** Meter display state for the Today header and usage sheet. */
export type MeterState = 'unlimited' | 'ample' | 'low' | 'exhausted';

export function meterState(check: QuotaCheck): MeterState {
  if (isUnlimited(check.total)) return 'unlimited';
  if (check.remaining <= 0) return 'exhausted';
  return check.remaining / check.total < 0.25 ? 'low' : 'ample';
}

// ── Paywall benefits ──────────────────────────────────────────────────────────

/**
 * What paying actually buys, named from the tier table rather than from memory.
 *
 * Every line here must be a capability `LIMITS` gives Plus and withholds from free.
 * One was not: all three paywalls advertised **"Every dialect — switch accents whenever
 * you like"**, and dialect is not gated anywhere. `patchCourse` updates it with no
 * entitlement check, `08-monetization` §2's tier table has no dialect row, §3's
 * free-tier boundary does not mention it, and strategy W3 treats dialect as a
 * differentiator carried into voice, thresholds and content — not as a lever to sell.
 *
 * So the product was asking to be paid for something every learner already had, on the
 * one screen where a false claim costs the most, in a product whose stated wedge is
 * that it does not assert what it cannot support. Nothing caught it: `verify:promises`
 * ties exactly one claim — the ten free voice minutes — to the code.
 *
 * Ported to `PaywallBenefits.swift` and `PaywallBenefits.kt` with the same cases, and
 * `verify:promises` now checks each line against the `LIMITS` field it claims.
 */

export interface PaywallBenefit {
  readonly title: string;
  readonly detail: string;
  /** The `TierLimits` field this line is a claim about. */
  readonly backedBy: keyof (typeof LIMITS)['free'];
}

export const PAYWALL_BENEFITS: readonly PaywallBenefit[] = [
  {
    title: 'Unlimited scored speaking',
    detail: 'No daily cap',
    backedBy: 'voiceSecondsPerDay',
  },
  {
    // Replaces the dialect line. This is the product's own differentiator and a real
    // Plus-only capability: free gets the five scores, Plus gets the phoneme table.
    title: 'Deep pronunciation analysis',
    detail: 'The phoneme table, and what to do about each sound',
    backedBy: 'deepAnalysis',
  },
  { title: 'Offline lessons', detail: 'Practise on a plane', backedBy: 'offlineDownloads' },
  {
    title: 'Your full report-card history',
    detail: 'Every session, not the last seven days',
    backedBy: 'historyDays',
  },
];

/**
 * True when the free tier genuinely lacks what a benefit claims.
 *
 * A benefit that free already has is not a benefit, and this is the check that was
 * missing.
 */
export function isGenuinePlusBenefit(benefit: PaywallBenefit): boolean {
  const free = LIMITS.free[benefit.backedBy];
  const plus = LIMITS.plus[benefit.backedBy];
  if (typeof free === 'boolean') return free === false && plus === true;
  if (typeof free === 'number' && typeof plus === 'number') return plus > free;
  return false;
}
